import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { CartProvider } from "@/context/CartContext";
import { AdminProvider } from "@/context/AdminContext";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import OtherPartners from "@/pages/Gateway";
import ProductDetail from "@/pages/ProductDetail";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import CartPage from "@/pages/CartPage";
import Checkout from "@/pages/Checkout";
import OrderConfirmation from "@/pages/OrderConfirmation";
import ReviewPage from "@/pages/ReviewPage";
import ReviewsPublic from "@/pages/ReviewsPublic";
import LeaveReview from "@/pages/LeaveReview";
import ShopAffiliates from "@/pages/ShopAffiliates";
import ShopAffiliatesGuides from "@/pages/ShopAffiliatesGuides";
import PolicyPage from "@/pages/PolicyPage";
import NotFound from "@/pages/NotFound";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminInventory from "@/pages/admin/AdminInventory";
import AdminImport from "@/pages/admin/AdminImport";
import PackingSlip from "@/pages/admin/PackingSlip";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminOrderDetail from "@/pages/admin/AdminOrderDetail";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminDropship from "@/pages/admin/AdminDropship";
import AdminAbout from "@/pages/admin/AdminAbout";
import AdminAffiliates from "@/pages/admin/AdminAffiliates";
import AdminProductRequests from "@/pages/admin/AdminProductRequests";

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => window.scrollTo(0, 0), [pathname]);
  return null;
}

function StoreLayout() {
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <CartProvider>
          <AdminProvider>
            <Routes>
              <Route element={<StoreLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/gateway" element={<ShopAffiliates />} />
                <Route path="/other-partners" element={<OtherPartners />} />
                <Route path="/product/:slug" element={<ProductDetail />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/order/:orderNumber" element={<OrderConfirmation />} />
                <Route path="/review/:token" element={<ReviewPage />} />
                <Route path="/reviews" element={<ReviewsPublic />} />
                <Route path="/leave-review" element={<LeaveReview />} />
                <Route path="/shop-affiliates" element={<Navigate to="/gateway" replace />} />
                <Route path="/shop-affiliates/guides" element={<ShopAffiliatesGuides />} />
                <Route path="/gateway/guides" element={<ShopAffiliatesGuides />} />
                <Route path="/policies/:slug" element={<PolicyPage />} />
                <Route path="*" element={<NotFound />} />
              </Route>

              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/orders/:orderNumber/packing-slip" element={<PackingSlip />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="inventory" element={<AdminInventory />} />
                <Route path="import" element={<AdminImport />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="orders/:orderNumber" element={<AdminOrderDetail />} />
                <Route path="reviews" element={<AdminReviews />} />
                <Route path="affiliates" element={<AdminAffiliates />} />
                <Route path="product-requests" element={<AdminProductRequests />} />
                <Route path="dropshipping" element={<AdminDropship />} />
                <Route path="about" element={<AdminAbout />} />
              </Route>
            </Routes>
          </AdminProvider>
        </CartProvider>
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
